<script setup lang="ts">
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { ref } from 'vue';

// -------------------------------
// 🔹 Composants
// -------------------------------
import BaseButtonPrimary from '@/components/common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Stores
// -------------------------------
import { useDialogStore } from '@/store/dialog.store';
import { CommerceMutation } from '@/mutations/commerce.mutations';
import { CategorieMutation } from '@/mutations/categorie.mutations';
import { FileMutation } from '@/mutations/file.mutation';
import { UserMutation } from '@/mutations/user.mutation';
import { TaxeMutation } from '@/mutations/taxe.mutation';

// -------------------------------
// 🔹 Utils & refs
// -------------------------------
import { content, isGlobalError } from '@/refs/common.refs';
import {
  commerceId,
  contactId,
  idToDelete,
  isAtcivatedWatch,
  isAtcivatedWatchContact,
  isDeletedFromConatct,
} from '@/refs/commerce.refs';
import { isNextStep_dialog } from '@/refs/dashboard.refs';
import { globalHeaderTitleLayout, messageDelete } from '../refs/common.refs';
import { isDeletedFromCategory } from '@/refs/categories.refs';
import { fileId, isComingFromFileDeleted } from '@/refs/file.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';
import {
  isAtcivatedWatchUser,
  isDeletedFromClient,
  isDeletedFromUser,
} from '@/refs/user.refs';
import { isAtcivatedWatchTaxe, isDeletedFromTaxe } from '@/refs/taxe.refs';
import { CurrencyMutation } from '@/mutations/currency.mutation';
import {
  isAtcivatedWatchCurrency,
  isDeletedFromCurrency,
} from '@/refs/currency.refs';
import {
  isAtcivatedHorraireCommerceWatch,
  isDeletedHorraireCommerce,
} from '@/refs/horraire.commerce.refs';
import { HorraireCommerceMutation } from '@/mutations/horraire.commerce.mutations';
import {
  isAtcivatedWatchPromotion,
  isDeletedFromPromotion,
  promotion_id,
} from '@/refs/promotion.refs';
import { PromotionMutation } from '@/mutations/promotion.mutation';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const dialogStore = useDialogStore();
const { handleDeleteCommerce, handleDeleteCommerceContact } =
  CommerceMutation();
const { handleDeleteCategorie } = CategorieMutation();
const { handleDeleteFile } = FileMutation();
const { handleDeleteUserClientCustomer, handleDeleteUser } = UserMutation();
const { handleDeleteTaxById } = TaxeMutation();
const { handleDeleteCurrency } = CurrencyMutation();

const { handleDeleteHorraireCommerce } = HorraireCommerceMutation();
const { handleDeletePromotion } = PromotionMutation();

// -------------------------------
// 🔹 Gestion du dialogue
// -------------------------------

/**
 * Passe à l’étape suivante dans le dialogue (stepper).
 */
const handleNextStep = (): void => {
  isNextStep_dialog.value = !isNextStep_dialog.value;
};

/**
 * Ferme le dialogue et réinitialise les valeurs associées.
 */
const handleClose = (): void => {
  dialogStore.closeDialog();
};

/**
 * Supprime un commerce en utilisant l’ID stocké dans `ref selon le feture`.
 */

const finalizeAction = (toggleRef: any, success = true, hideDialog = true) => {
  setTimeout(() => {
    isLoaderLoginFormActivated.value = false;
    toggleRef.value = !toggleRef.value;
    if (success && hideDialog) {
      dialogStore.visible = false;
    } else if (!success) {
      isGlobalError.value = true;
    }
  }, 1000);
};

const handleDeleteFileAsync = async () => {
  if (fileId.value) {
    try {
      isLoaderLoginFormActivated.value = true;
      const res = await handleDeleteFile(fileId.value);
      if (res) {
        setTimeout(() => {
          isLoaderLoginFormActivated.value = false;
          isAtcivatedWatch.value = !isAtcivatedWatch.value;
          dialogStore.visible = false;
        }, 1000);
      }
    } catch (error) {
      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
        isGlobalError.value = false;
        isAtcivatedWatch.value = !isAtcivatedWatch.value;
        isGlobalError.value = false;
      }, 1000);
    }
  }
};

const handleDeleteClient = async () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const res = await handleDeleteUserClientCustomer();
    finalizeAction(isAtcivatedWatch, !!res);
  } catch (error) {
    finalizeAction(isAtcivatedWatch, false);
  }
};

const handleDeleteTaxe = async () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const res = await handleDeleteTaxById();
    finalizeAction(isAtcivatedWatchTaxe, !!res);
  } catch (error) {
    finalizeAction(isAtcivatedWatchTaxe, false);
  }
};

const handleDeleteCur = async () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const res = await handleDeleteCurrency();
    finalizeAction(isAtcivatedWatchCurrency, !!res);
  } catch (error) {
    finalizeAction(isAtcivatedWatchCurrency, false);
  }
};

const handleDeleteHorraireCom = async () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const res = await handleDeleteHorraireCommerce();
    finalizeAction(isAtcivatedHorraireCommerceWatch, !!res);
  } catch (error) {
    finalizeAction(isAtcivatedHorraireCommerceWatch, false);
  }
};

const handleDeleComerce = async () => {
  if (!idToDelete.value) return;

  try {
    isLoaderLoginFormActivated.value = true;
    await handleDeleteCommerce(idToDelete.value);
    finalizeAction(isAtcivatedWatch);
  } catch (error) {
    finalizeAction(isAtcivatedWatch, false, false);
  }
};

const handleDeleComerceContact = async () => {
  if (!commerceId.value && contactId.value) return;

  try {
    isLoaderLoginFormActivated.value = true;
    await handleDeleteCommerceContact();
    finalizeAction(isAtcivatedWatchContact);
  } catch (error) {
    finalizeAction(isAtcivatedWatch, false, false);
  }
};

const handleDeletePromo = async () => {
  if (!promotion_id.value) return;

  try {
    isLoaderLoginFormActivated.value = true;
    await handleDeletePromotion();
    finalizeAction(isAtcivatedWatchPromotion);
  } catch (error) {
    finalizeAction(isAtcivatedWatchPromotion, false, false);
  }
};

const handleDeleteUserValue = async () => {
  console.log('Deleting user...');
  try {
    isLoaderLoginFormActivated.value = true;
    const res = await handleDeleteUser();
    finalizeAction(isAtcivatedWatchUser, !!res);
  } catch (error) {
    finalizeAction(isAtcivatedWatchUser, false);
  }
};

const handleDelete = async () => {
  const actions = [
    {
      condition: isDeletedFromUser.value,
      action: handleDeleteUserValue,
    },
    {
      condition: isDeletedFromCategory.value && idToDelete.value,
      action: handleDeleteCategorie,
    },
    {
      condition: isComingFromFileDeleted.value,
      action: handleDeleteFileAsync,
    },
    {
      condition: isDeletedFromClient.value,
      action: handleDeleteClient,
    },
    {
      condition: isDeletedFromTaxe.value,
      action: handleDeleteTaxe,
    },
    {
      condition: isDeletedFromCurrency.value,
      action: handleDeleteCur,
    },
    {
      condition: isDeletedHorraireCommerce.value,
      action: handleDeleteHorraireCom,
    },
    {
      condition: isDeletedFromConatct.value,
      action: handleDeleComerceContact,
    },
    {
      condition: isDeletedFromPromotion,
      action: handleDeletePromo,
    },

    {
      condition: true,
      action: handleDeleComerce,
    },
  ];

  const match = actions.find((a) => a.condition);
  if (match) {
    await match.action();
  }
};
</script>

<template>
  <div>
    <p
      class="text-decroissance text-14px font-tt-medium-Normal text-center leading-normal"
    >
      {{ messageDelete }}
    </p>
    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="isNextStep_dialog ? 'Fermer' : 'Fermer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleDelete">
          <BaseButtonPrimary
            :isClicked="isLoaderLoginFormActivated"
            type="button"
            :label="'Supprimer'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>
